import { forwardRef } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Square, icon-only button. Used for table row actions, panel close
 * affordances, toolbar utilities (column-visibility, density toggle).
 *
 * Always set `aria-label` — there is no visible text fallback. The
 * label is also rendered as the native `title` so hover tooltips stay
 * consistent.
 */
export type IconButtonVariant = "default" | "danger" | "active";
export type IconButtonSize = "sm" | "md";

const SIZE_CLS: Record<IconButtonSize, string> = {
  sm: "h-7 w-7 rounded-md",
  md: "h-8 w-8 rounded-md",
};

const ICON_SIZE: Record<IconButtonSize, number> = { sm: 13, md: 14 };

const VARIANT_CLS: Record<IconButtonVariant, string> = {
  default: "text-fg-muted hover:text-fg hover:bg-bg-hover",
  danger:  "text-fg-muted hover:text-status-critical hover:bg-status-critical/10",
  // `active` represents pressed/toggled state (e.g. split-view enabled,
  // density toggle on) — uses the same brand-tinted treatment as a
  // selected row so the operator's mental model is consistent.
  active:  "text-fg bg-bg-elev border border-line-subtle",
};

export type IconButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  icon: LucideIcon;
  /** Required — describes the action for screen readers and tooltips. */
  label: string;
  size?: IconButtonSize;
  variant?: IconButtonVariant;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, label, size = "md", variant = "default", className, type = "button", ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      title={label}
      aria-label={label}
      className={cn(
        "grid place-items-center transition focus-ring disabled:opacity-50 disabled:cursor-not-allowed",
        SIZE_CLS[size],
        VARIANT_CLS[variant],
        className,
      )}
      {...rest}
    >
      <Icon size={ICON_SIZE[size]} />
    </button>
  );
});
